import { useNavigate } from 'react-router';
import { Image, MapPin, MessageCircle, TriangleAlert } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { AmountDisplay } from '@/components/common/AmountDisplay';
import { CategoryIcon } from '@/components/common/CategoryIcon';
import { ExpenseShareLabel } from '@/components/expenses/ExpenseShareLabel';
import { format } from 'date-fns';
import type { ExpenseWithGroup } from '@/api/expenses';
import { useTranslation } from 'react-i18next';

interface ExpenseCardListProps {
  expenses: ExpenseWithGroup[];
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  selectionMode: boolean;
  commentCounts?: Record<string, number>;
}

export function ExpenseCardList({
  expenses,
  selectedIds,
  onToggleSelect,
  selectionMode,
  commentCounts,
}: ExpenseCardListProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div className="space-y-2">
      {expenses.map(expense => {
        const isSelected = selectedIds.has(expense.id);

        return (
          <button
            key={expense.id}
            onClick={() => {
              if (selectionMode) {
                onToggleSelect(expense.id);
              } else {
                navigate(`/expenses/${expense.id}`);
              }
            }}
            className={`flex w-full items-center gap-3 rounded-lg bg-surface-primary p-4 shadow-sm ring-1 ring-border-primary text-left hover:shadow-md transition-shadow ${
              isSelected ? 'ring-brand-500 ring-2' : ''
            }`}
          >
            {selectionMode && (
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => onToggleSelect(expense.id)}
                onClick={e => e.stopPropagation()}
                className="h-4 w-4 shrink-0 rounded border-border-secondary text-brand-600 focus:ring-brand-500"
              />
            )}

            <CategoryIcon
              icon={expense.category?.icon}
              color={expense.category?.color}
            />

            <div className="flex-1 min-w-0">
              <p className="font-medium text-text-primary truncate">{expense.description}</p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <p className="text-xs text-text-tertiary">
                  {t('expenses:detail.paidBy')} {expense.paid_by_user?.display_name ?? t('web:common.unknownUser')} &middot;{' '}
                  {format(new Date(expense.expense_date), 'MMM d, yyyy')}
                </p>
                {(expense.has_open_dispute || commentCounts?.[expense.id] || expense.receipt_url || expense.location_name || expense.latitude) && (
                  <div className="flex items-center gap-1">
                    {expense.has_open_dispute && (
                      <span className="inline-flex items-center gap-0.5 rounded-full bg-red-500/15 px-1.5 py-0.5 text-red-600 dark:text-red-400" title={t('web:common.hasOpenDispute')}>
                        <TriangleAlert className="h-3 w-3" />
                        <span className="text-[10px] font-medium">{t('expenses:swipeable.dispute')}</span>
                      </span>
                    )}
                    {commentCounts?.[expense.id] && (
                      <span className="inline-flex items-center gap-0.5 rounded-full bg-blue-500/15 px-1.5 py-0.5 text-blue-600 dark:text-blue-400" title={t('web:common.comments', { count: commentCounts[expense.id] })}>
                        <MessageCircle className="h-3 w-3" />
                        <span className="text-[10px] font-medium">{commentCounts[expense.id]}</span>
                      </span>
                    )}
                    {expense.receipt_url && (
                      <span className="inline-flex items-center rounded-full bg-emerald-500/15 p-1 text-emerald-600 dark:text-emerald-400" title={t('web:common.hasReceipt')}>
                        <Image className="h-3 w-3" />
                      </span>
                    )}
                    {(expense.location_name || expense.latitude) && (
                      <span className="inline-flex items-center rounded-full bg-amber-500/15 p-1 text-amber-600 dark:text-amber-400" title={expense.location_name ?? t('web:common.hasLocation')}>
                        <MapPin className="h-3 w-3" />
                      </span>
                    )}
                  </div>
                )}
              </div>
              {expense.group && (
                <div className="flex items-center gap-1 mt-1">
                  <span
                    className="inline-block h-2 w-2 rounded-full"
                    style={{ backgroundColor: expense.group.color ?? '#6366F1' }}
                  />
                  <span className="text-xs text-text-quaternary">{expense.group.name}</span>
                </div>
              )}
            </div>

            <div className="text-right shrink-0">
              <AmountDisplay
                amountMinor={Number(expense.amount_minor_src)}
                currency={expense.currency_code_src}
                className="font-semibold text-text-primary"
              />
              <ExpenseShareLabel expense={expense} userId={user?.id} />
            </div>
          </button>
        );
      })}
    </div>
  );
}
